/**
 * useCountUp
 *
 * The real number is rendered first, always. The count-up from zero is
 * decoration: it runs once when the element scrolls into view, and never
 * runs for a reader who prefers reduced motion or for the pre-renderer.
 */

import { useState, useEffect, useRef } from 'react';

const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

const motionAllowed = () => {
  if (typeof window === 'undefined') return false;
  if (typeof navigator !== 'undefined' && /ReactSnap|HeadlessChrome/i.test(navigator.userAgent)) return false;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return typeof IntersectionObserver !== 'undefined';
};

const useCountUp = ({ end, duration = 1600, decimals = 0 }) => {
  const [displayValue, setDisplayValue] = useState(end);
  const countRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current || !motionAllowed() || !countRef.current) return undefined;

    let frame = null;
    const run = () => {
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        setDisplayValue(Number((easeOutQuart(progress) * end).toFixed(decimals)));
        if (progress < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !doneRef.current) {
        doneRef.current = true;
        setDisplayValue(0);
        run();
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(countRef.current);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [end, duration, decimals]);

  return { countRef, displayValue };
};

export default useCountUp;
